import { API } from "@/main.js";

const getDefaultState = () => {
  return {
    tabIsDisabled: true,
    tabIsLoading: true,

    maskGridUnitSize: 26,
    maskGridUnits: [],

    sideSection: "toolbar",

    ductEntryMode: undefined,

    masks: [],
    selectedMaskIndex: 0,
    selectedDuctEntryIndex: undefined,

    selectedDuctEntryType: undefined,

    ductEntryTypeList: []
  };
};

// initial state
const state = getDefaultState();

const getters = {
  isManholeMasksTabDisabled: state => state.tabIsDisabled,
  isMasksTabLoading: state => state.tabIsLoading,
  getMaskGridUnitSize: state => state.maskGridUnitSize,
  getMaskGridUnits: state => state.maskGridUnits,
  getDuctTypeList: state => state.ductEntryTypeList,
  getManholeMasks: state => state.masks,
  getSelectedMaskIndex: state => state.selectedMaskIndex,
  getSelectedMask: state => {
    if (state.masks.length > 0) {
      return state.masks[state.selectedMaskIndex];
    } else return undefined;
  },
  getSelectedDuctEntryIndex: state => state.selectedDuctEntryIndex,
  getSelectedDuctEntry: state => {
    if (
      state.masks.length > 0 &&
      state.masks[state.selectedMaskIndex].duct_entries.length > 0
    ) {
      return state.masks[state.selectedMaskIndex].duct_entries[
        state.selectedDuctEntryIndex
      ];
    } else return undefined;
  },
  getDuctEntryByIndex: state => ductEntryIndex => {
    if (
      state.masks.length > 0 &&
      state.masks[state.selectedMaskIndex].duct_entries.length > 0
    ) {
      return state.masks[state.selectedMaskIndex].duct_entries[ductEntryIndex];
    } else return undefined;
  },
  getSelectedDuctEntryType: state => state.selectedDuctEntryType,
  getMasksTabSideSection: state => state.sideSection,
  getMaskDuctEntryMode: state => state.ductEntryMode
};

const mutations = {
  RESET_MASKS_STATE(state) {
    Object.assign(state, getDefaultState());
  },

  SET_DUCT_TYPE_LIST(state, ductEntryTypeList) {
    state.ductEntryTypeList = ductEntryTypeList;
  },

  SET_MASKS_TAB_LOADING(state, isLoading) {
    state.tabIsLoading = isLoading;
  },

  SET_GRID_UNITS(state, gridUnits) {
    state.maskGridUnits = gridUnits;
  },
  SET_MASKS_TAB_SIDE_SECTION(state, section) {
    state.sideSection = section;
  },
  SET_MANHOLE_MASKS(state, masks) {
    state.masks = masks;
  },
  SET_SELECTED_MASK_INDEX(state, maskIndex) {
    state.selectedMaskIndex = maskIndex;
  },
  SET_SELECTED_DUCT_ENTRY_INDEX(state, ductEntryIndex) {
    state.selectedDuctEntryIndex = ductEntryIndex;
  },
  SET_SELECTED_DUCT_ENTRY_TYPE(state, ductEntryType) {
    state.selectedDuctEntryType = ductEntryType;
  },
  SET_DUCT_ENTRY_MODE(state, mode) {
    state.ductEntryMode = mode;
  },

  SET_TAB_IS_DISABLE(state, mode) {
    state.tabIsDisabled = mode;
  },

  EDIT_MASK(state, dataToUpdate) {
    dataToUpdate.forEach(update => {
      state.masks[state.selectedMaskIndex][update.field] = update.value;
    });
  },

  ADD_DUCT_ENTRY(state, ductEntry) {
    if (state.masks[state.selectedMaskIndex].duct_entries) {
      state.masks[state.selectedMaskIndex].duct_entries.push(ductEntry);
    } else {
      state.masks[state.selectedMaskIndex].duct_entries = [ductEntry];
    }
  },
  EDIT_DUCT_ENTRY(state, dataToUpdate) {
    dataToUpdate.forEach(update => {
      state.masks[state.selectedMaskIndex].duct_entries[
        state.selectedDuctEntryIndex
      ][update.field] = update.value;
    });
  },
  REMOVE_DUCT_ENTRY(state, ductEntryIndex) {
    state.masks[state.selectedMaskIndex].duct_entries.splice(ductEntryIndex, 1);
  },

  MIRROR_MASK(state, maskDestination) {
    let maskIndex = state.masks.findIndex(
      mask => mask.mk_label === maskDestination.mk_label
    );
    state.masks[maskIndex] = maskDestination;
  }
};

const actions = {
  resetMasksState({ commit }) {
    commit("RESET_MASKS_STATE");
  },

  fetchDuctTypeList: ({ commit }) => {
    let listName = "list_duct_type";
    return new Promise((resolve, reject) => {
      API.lists.getByType(
        listName,
        undefined,
        result => {
          let list = result.data.data[listName];
          list.sort(compareDuctEntrySizes);
          list.forEach(ductType => {
            prepareDuctTypeSizes(ductType);
          });
          commit("SET_DUCT_TYPE_LIST", list);
          resolve(list);
        },
        error => {
          reject(error);
        }
      );
    });
  },

  setMasksTabLoading({ commit }, isLoading) {
    commit("SET_MASKS_TAB_LOADING", isLoading);
  },

  setMaskGridUnits({ commit }, gridUnits) {
    commit("SET_GRID_UNITS", gridUnits);
  },

  setMasksTabSideSection({ commit }, section) {
    commit("SET_MASKS_TAB_SIDE_SECTION", section);
  },

  setManholeMasks({ commit }, masks) {
    commit("SET_MANHOLE_MASKS", masks);
  },

  setTabDisabled({ commit }, mode) {
    commit("SET_TAB_IS_DISABLE", mode);
  },

  editMask({ commit }, dataToUpdate) {
    commit("EDIT_MASK", dataToUpdate);
  },

  addDuctEntry({ commit }, ductEntry) {
    commit("ADD_DUCT_ENTRY", ductEntry);
  },

  editDuctEntry({ commit }, dataToUpdate) {
    commit("EDIT_DUCT_ENTRY", dataToUpdate);
  },

  removeDuctEntry({ commit }, ductEntryIndex) {
    commit("REMOVE_DUCT_ENTRY", ductEntryIndex);
  },

  setSelectedMaskIndex({ commit }, maskIndex) {
    commit("SET_SELECTED_MASK_INDEX", maskIndex);
    commit("SET_MASKS_TAB_LOADING", true);
  },

  setSelectedDuctEntryIndex({ commit }, ductEntryIndex) {
    commit("SET_SELECTED_DUCT_ENTRY_INDEX", ductEntryIndex);
    if (ductEntryIndex === undefined) {
      commit("SET_DUCT_ENTRY_MODE", undefined);
    }
  },

  setSelectedDuctEntryType({ commit }, ductEntryType) {
    commit("SET_SELECTED_DUCT_ENTRY_TYPE", ductEntryType);
  },

  setDuctEntryMode({ commit }, mode) {
    commit("SET_DUCT_ENTRY_MODE", mode);
  },

  setMirrorMask({ commit }, destinationMask) {
    commit("MIRROR_MASK", destinationMask);
  }
};

function compareDuctEntrySizes(a, b) {
  if (a.width < b.width) {
    return -1;
  }
  if (a.width > b.width) {
    return 1;
  }
  return 0;
}

function prepareDuctTypeSizes(ductType) {
  let width = parseFloat(ductType.width);
  let containerWidth = Math.round(width);
  ductType.width = width;
  ductType.containerWidth = containerWidth;

  if (ductType.height !== null) {
    ductType.height = parseFloat(ductType.height);
    ductType.containerHeight = Math.round(ductType.height);
  } else {
    ductType.containerHeight = containerWidth;
  }
}

export default { state, getters, actions, mutations };



// WEBPACK FOOTER //
// ./src/store/modules/masks.js